import { openRing, validateRing } from '@/shared/domain/zoneEditor'
import { SectorError, type SectorRepository } from '../application/SectorRepository'
import {
  type CreateSectorCommand,
  type DraftValidation,
  type GeometryIssue,
  type PublicationContent,
  type PublicationPreview,
  type PublishCommand,
  type PublishResult,
  type PublishedAvailability,
  type SectorGeometry,
  isSeasonLabel,
  isSectorCode,
  type DraftAvailability,
  type SaveAvailabilityCommand,
  type SaveSectorAreaCommand,
  type Sector,
  type SectorWorkspace,
  type SetSectorArchivedCommand,
  type UpdateSectorCommand,
} from '../domain/sector'

/** Texte vide ou absent → nul, comme `nullif(btrim(...), '')` côté serveur. */
function blankToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed === undefined || trimmed === '' ? null : trimmed
}

/** Retour approximatif hors PostGIS, avec le moteur commun de l'éditeur (anneau par anneau). */
function approximateIssues(geometry: SectorGeometry | null): GeometryIssue[] {
  if (geometry === null) return []
  const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates
  const issues: GeometryIssue[] = []
  for (const ring of polygons.flat()) {
    const issue = validateRing(openRing(ring))
    if (issue === 'TOO_FEW_VERTICES' || issue === 'EMPTY_AREA') issues.push({ code: issue })
    else if (issue !== null) issues.push({ code: 'INVALID' })
  }
  return issues
}

/**
 * Sans Supabase (développement local, captures) : brouillon en mémoire, vide au départ. Aucun secteur ni contour
 * n'est fabriqué; les règles du serveur (révision, code unique, archivage) sont reproduites pour que l'écran se
 * comporte de la même façon.
 */
export function createFixtureSectorRepository(now: () => Date = () => new Date()): SectorRepository {
  let revision = 0
  let sectors: Sector[] = []
  let availability: DraftAvailability[] = []
  let published: PublishedAvailability[] = []
  let publishedSectors = new Map<string, { name: string; color: string; geometry: SectorGeometry | null }>()
  let latestPublication: SectorWorkspace['latestPublication'] = null
  let publications: PublicationContent[] = []
  let snapshots: {
    id: string
    revision: number
    reason: string
    createdAt: string
    createdBy: string | null
  }[] = []
  let restoredFrom: { id: string; version: number } | null = null
  const restoredOf = new Map<string, number>()
  let sequence = 0

  function take(expected: number): number {
    if (expected !== revision) throw new SectorError('SECTOR_DRAFT_CONFLICT')
    revision += 1
    return revision
  }

  function activeSector(sectorId: string): Sector {
    const sector = sectors.find((item) => item.id === sectorId)
    if (!sector) throw new SectorError('SECTOR_NOT_FOUND')
    if (sector.archivedAt !== null) throw new SectorError('SECTOR_ARCHIVED')
    return sector
  }

  function nameTaken(name: string, exceptId?: string): boolean {
    const key = name.trim().toLowerCase()
    return sectors.some(
      (item) => item.archivedAt === null && item.id !== exceptId && item.name.trim().toLowerCase() === key,
    )
  }

  function replace(sector: Sector): void {
    sectors = sectors.map((item) => (item.id === sector.id ? sector : item))
  }

  /** Même contrat que le dépôt Supabase : une règle enfreinte rejette la promesse. */
  function settle<T>(work: () => T): Promise<T> {
    try {
      return Promise.resolve(work())
    } catch (error) {
      return Promise.reject(error instanceof Error ? error : new SectorError('UNEXPECTED'))
    }
  }

  const workspace = (): SectorWorkspace => ({
    revision,
    basePublicationId: latestPublication?.id ?? null,
    latestPublication: structuredClone(latestPublication),
    sectors: structuredClone(sectors),
    draftAvailability: structuredClone(availability),
    publishedAvailability: structuredClone(published),
    referenceBoundaries: [],
  })

  /** Comparatif sans PostGIS ni contrats : contours et disponibilités seulement, aucun compte d'adresses inventé. */
  const preview = (): PublicationPreview => {
    const active = sectors.filter((sector) => sector.archivedAt === null)
    const sectorChanges: PublicationPreview['sectors'] = []
    for (const sector of active) {
      const before = publishedSectors.get(sector.id)
      const geometry = sector.draftArea?.geometry ?? null
      const change = !before
        ? 'ADDED'
        : JSON.stringify(before.geometry) !== JSON.stringify(geometry)
          ? 'GEOMETRY_CHANGED'
          : before.name !== sector.name || before.color !== sector.color
            ? 'RENAMED'
            : null
      if (change) {
        sectorChanges.push({
          sectorId: sector.id,
          name: sector.name,
          change,
          areaBeforeM2: null,
          areaAfterM2: null,
          verticesBefore: 0,
          verticesAfter: 0,
          hasContour: geometry !== null,
        })
      }
    }
    for (const [sectorId, before] of publishedSectors) {
      if (!active.some((sector) => sector.id === sectorId)) {
        sectorChanges.push({
          sectorId,
          name: before.name,
          change: 'REMOVED',
          areaBeforeM2: null,
          areaAfterM2: null,
          verticesBefore: 0,
          verticesAfter: 0,
          hasContour: false,
        })
      }
    }
    const key = (cell: { sectorId: string; service: string; season: string }) =>
      `${cell.sectorId}|${cell.service}|${cell.season}`
    const draftCells = availability.filter((cell) => active.some((sector) => sector.id === cell.sectorId))
    const keys = new Set([...draftCells.map(key), ...published.map(key)])
    const availabilityChanges: PublicationPreview['availability'] = []
    for (const cellKey of keys) {
      const draft = draftCells.find((cell) => key(cell) === cellKey)
      const before = published.find((cell) => key(cell) === cellKey)
      const reference = draft ?? before
      if (!reference) continue
      if (draft?.status === before?.status && draft?.publicText === before?.publicText) continue
      availabilityChanges.push({
        sectorId: reference.sectorId,
        service: reference.service,
        season: reference.season,
        before: before?.status ?? null,
        after: draft?.status ?? null,
        publicTextChanged: draft?.publicText !== before?.publicText,
      })
    }
    return {
      basePublicationId: latestPublication?.id ?? null,
      baseVersion: latestPublication?.version ?? null,
      draftRevision: revision,
      draftBasePublicationId: latestPublication?.id ?? null,
      hasChanges: sectorChanges.length > 0 || availabilityChanges.length > 0,
      sectors: sectorChanges,
      availability: availabilityChanges,
      addresses: { added: 0, removed: 0, located: 0, bySector: [] },
      contractsConcerned: 0,
      exceptions: [],
      leadsChanged: 0,
      unlocated: 0,
      blocking: active
        .filter((sector) => approximateIssues(sector.draftArea?.geometry ?? null).length > 0)
        .map((sector) => ({ code: 'GEOMETRY_INVALID' as const, sectorId: sector.id })),
      warnings: active
        .filter(
          (sector) =>
            (sector.draftArea?.geometry ?? null) === null &&
            draftCells.some((cell) => cell.sectorId === sector.id),
        )
        .map((sector) => ({ code: 'AVAILABILITY_WITHOUT_CONTOUR' as const, sectorId: sector.id })),
      computedAt: now().toISOString(),
    }
  }

  const publish = (command: PublishCommand): PublishResult => {
    if (command.reason.trim() === '') throw new SectorError('SECTOR_PUBLICATION_REASON_REQUIRED')
    if (!isSeasonLabel(command.publicSeason)) throw new SectorError('SECTOR_PUBLIC_SEASON_INVALID')
    if (command.expectedRevision !== revision) throw new SectorError('SECTOR_DRAFT_CONFLICT')
    if (command.basePublicationId !== (latestPublication?.id ?? null)) {
      throw new SectorError('SECTOR_PUBLICATION_CONFLICT')
    }
    const diff = preview()
    if (diff.blocking.length > 0) throw new SectorError('SECTOR_PUBLICATION_BLOCKED')
    if (!diff.hasChanges) throw new SectorError('SECTOR_NOTHING_TO_PUBLISH')
    const version = (latestPublication?.version ?? 0) + 1
    latestPublication = {
      id: `fixture-publication-${String(version)}`,
      version,
      publicSeason: command.publicSeason,
      reason: command.reason.trim(),
      publishedAt: now().toISOString(),
      publishedBy: 'fixture-user',
    }
    const active = sectors.filter((sector) => sector.archivedAt === null)
    publishedSectors = new Map(
      active.map((sector) => [
        sector.id,
        {
          name: sector.name,
          color: sector.color,
          geometry: structuredClone(sector.draftArea?.geometry ?? null),
        },
      ]),
    )
    sectors = sectors.map((sector) => ({
      ...sector,
      publishedArea: publishedSectors.get(sector.id)?.geometry ?? null,
    }))
    published = availability
      .filter((cell) => active.some((sector) => sector.id === cell.sectorId))
      .map(({ sectorId, service, season, status, publicText }) => ({
        sectorId,
        service,
        season,
        status,
        publicText,
      }))
    publications = [
      {
        id: latestPublication.id,
        version,
        publicSeason: command.publicSeason,
        publishedAt: latestPublication.publishedAt,
        publishedBy: 'Démonstration',
        reason: latestPublication.reason,
        sectors: active.map((sector) => ({
          sectorId: sector.id,
          code: sector.code,
          name: sector.name,
          color: sector.color,
          provenance: sector.draftArea?.provenance ?? 'MANUAL',
          geometry: structuredClone(sector.draftArea?.geometry ?? null),
        })),
        availability: structuredClone(published),
      },
      ...publications,
    ]
    if (restoredFrom) restoredOf.set(latestPublication.id, restoredFrom.version)
    restoredFrom = null
    revision += 1
    return { publicationId: latestPublication.id, version, revision }
  }

  const validation = (): DraftValidation => ({
    revision,
    sectors: sectors
      .filter((sector) => sector.archivedAt === null)
      .map((sector) => ({
        sectorId: sector.id,
        hasGeometry: sector.draftArea?.geometry != null,
        issues: approximateIssues(sector.draftArea?.geometry ?? null),
      })),
    // Sans PostGIS, aucun chevauchement n'est calculé : la publication réelle reste contrôlée par le serveur.
    overlaps: [],
  })

  const commands = {
    createSector(command: CreateSectorCommand) {
      const code = command.code.trim()
      const name = command.name.trim()
      if (!isSectorCode(code)) throw new SectorError('SECTOR_CODE_INVALID')
      if (name.length < 1 || name.length > 100) throw new SectorError('SECTOR_NAME_INVALID')
      if (sectors.some((item) => item.code === code)) throw new SectorError('SECTOR_CODE_TAKEN')
      if (nameTaken(name)) throw new SectorError('SECTOR_NAME_TAKEN')
      const next = take(command.expectedRevision)
      sequence += 1
      const id = `fixture-sector-${String(sequence)}`
      sectors = [
        ...sectors,
        {
          id,
          code,
          name,
          color: command.color,
          internalDescription: blankToNull(command.internalDescription),
          archivedAt: null,
          updatedAt: now().toISOString(),
          draftArea: null,
          publishedArea: null,
        },
      ]
      return { sectorId: id, revision: next }
    },

    updateSector(command: UpdateSectorCommand) {
      const sector = activeSector(command.sectorId)
      const name = command.name?.trim() ?? sector.name
      if (name.length < 1 || name.length > 100) throw new SectorError('SECTOR_NAME_INVALID')
      if (nameTaken(name, sector.id)) throw new SectorError('SECTOR_NAME_TAKEN')
      const next = take(command.expectedRevision)
      replace({
        ...sector,
        name,
        color: command.color ?? sector.color,
        internalDescription:
          command.internalDescription === undefined
            ? sector.internalDescription
            : blankToNull(command.internalDescription),
        updatedAt: now().toISOString(),
      })
      return { sectorId: sector.id, revision: next }
    },

    setSectorArchived(command: SetSectorArchivedCommand) {
      const sector = sectors.find((item) => item.id === command.sectorId)
      if (!sector) throw new SectorError('SECTOR_NOT_FOUND')
      if ((sector.archivedAt !== null) === command.archived) {
        return { sectorId: sector.id, revision }
      }
      if (!command.archived && nameTaken(sector.name, sector.id)) throw new SectorError('SECTOR_NAME_TAKEN')
      const next = take(command.expectedRevision)
      replace({ ...sector, archivedAt: command.archived ? now().toISOString() : null })
      return { sectorId: sector.id, revision: next }
    },

    saveArea(command: SaveSectorAreaCommand) {
      const sector = activeSector(command.sectorId)
      const provenance = command.provenance ?? 'MANUAL'
      if ((provenance === 'REFERENCE_COPY') !== (command.referenceBoundaryId !== undefined)) {
        throw new SectorError('SECTOR_PROVENANCE_INVALID')
      }
      if (command.referenceBoundaryId !== undefined) throw new SectorError('SECTOR_REFERENCE_NOT_FOUND')
      const next = take(command.expectedRevision)
      replace({
        ...sector,
        draftArea: {
          geometry: command.geometry === null ? null : structuredClone(command.geometry),
          provenance,
          referenceBoundaryId: null,
          provenanceNote: blankToNull(command.provenanceNote),
          updatedAt: now().toISOString(),
        },
      })
      return { sectorId: sector.id, revision: next }
    },

    saveAvailability(command: SaveAvailabilityCommand) {
      if (command.items.length < 1 || command.items.length > 300) {
        throw new SectorError('AVAILABILITY_COMMAND_INVALID')
      }
      for (const item of command.items) {
        activeSector(item.sectorId)
        if (!isSeasonLabel(item.season)) throw new SectorError('AVAILABILITY_SEASON_INVALID')
      }
      const next = take(command.expectedRevision)
      for (const item of command.items) {
        const rest = availability.filter(
          (cell) =>
            !(
              cell.sectorId === item.sectorId &&
              cell.service === item.service &&
              cell.season === item.season
            ),
        )
        availability =
          item.status === null
            ? rest
            : [
                ...rest,
                {
                  sectorId: item.sectorId,
                  service: item.service,
                  season: item.season,
                  status: item.status,
                  publicText: blankToNull(item.publicText),
                  internalNote: blankToNull(item.internalNote),
                  updatedAt: now().toISOString(),
                },
              ]
      }
      return { revision: next }
    },
  }

  return {
    getWorkspace: () => settle(workspace),
    getDraftValidation: () => settle(validation),
    getPublicationPreview: () => settle(preview),
    getHistory: () =>
      settle(() => ({
        draft: {
          revision,
          updatedAt: null,
          updatedBy: null,
          basePublicationId: latestPublication?.id ?? null,
          restoredFromVersion: restoredFrom?.version ?? null,
        },
        publications: publications.map((item) => ({
          id: item.id,
          version: item.version,
          publishedAt: item.publishedAt,
          publishedBy: item.publishedBy,
          reason: item.reason,
          publicSeason: item.publicSeason,
          restoredFromVersion: restoredOf.get(item.id) ?? null,
          sectorChanges: item.sectors.length,
          availabilityChanges: item.availability.length,
        })),
        snapshots: structuredClone(snapshots),
      })),
    getPublication: (publicationId) =>
      settle(() => {
        const found = publications.find((item) => item.id === publicationId)
        if (!found) throw new SectorError('SECTOR_PUBLICATION_NOT_FOUND')
        return structuredClone(found)
      }),
    restorePublication: (command) =>
      settle(() => {
        if (command.reason.trim() === '') throw new SectorError('SECTOR_RESTORE_REASON_REQUIRED')
        const found = publications.find((item) => item.id === command.publicationId)
        if (!found) throw new SectorError('SECTOR_PUBLICATION_NOT_FOUND')
        const next = take(command.expectedRevision)
        snapshots = [
          {
            id: `fixture-snapshot-${String(snapshots.length + 1)}`,
            revision: next - 1,
            reason: `Avant la restauration de v${String(found.version)} : ${command.reason.trim()}`,
            createdAt: now().toISOString(),
            createdBy: 'Démonstration',
          },
          ...snapshots,
        ]
        sectors = sectors.map((sector) => {
          const restored = found.sectors.find((item) => item.sectorId === sector.id)
          if (!restored)
            return sector.archivedAt === null ? { ...sector, archivedAt: now().toISOString() } : sector
          return {
            ...sector,
            name: restored.name,
            color: restored.color,
            archivedAt: null,
            draftArea: {
              geometry: structuredClone(restored.geometry),
              provenance: restored.provenance,
              referenceBoundaryId: null,
              provenanceNote: `Restauré de v${String(found.version)}`,
              updatedAt: now().toISOString(),
            },
          }
        })
        availability = found.availability.map((cell) => ({
          ...cell,
          internalNote: null,
          updatedAt: now().toISOString(),
        }))
        restoredFrom = { id: found.id, version: found.version }
        return { revision: next }
      }),
    // Démonstration : aucune demande n'est évaluée, rien n'est inventé.
    getLeadSector: (leadId) =>
      Promise.resolve({
        leadId,
        hasPosition: false,
        sectorId: null,
        sectorName: null,
        status: null,
        source: null,
        checkedAt: null,
        intentSectorId: null,
        intentSectorName: null,
        requestIntent: null,
        history: [],
      }),
    getLeadSectors: (leadIds) =>
      Promise.resolve(leadIds.map((leadId) => ({ leadId, sectorId: null, status: null }))),
    recheckLeadSector: () => Promise.reject(new SectorError('SECTOR_NO_PUBLICATION')),
    setLeadSector: () => Promise.reject(new SectorError('LEAD_NOT_FOUND')),
    publish: (command) => settle(() => publish(command)),
    // Sans PostGIS ni contrats en local : aucune classification n'est inventée.
    classifyPoint: () =>
      Promise.resolve({ status: 'unverified', sectorId: null, reason: 'NO_POSITION', candidates: [] }),
    getServiceLocations: () => Promise.resolve([]),
    getRouteSectors: () => Promise.resolve([]),
    setLocationOverride: () => Promise.reject(new SectorError('SERVICE_LOCATION_NOT_FOUND')),
    // Aucune limite de référence en local : rien à copier.
    copyReference: () => Promise.reject(new SectorError('SECTOR_REFERENCE_NOT_FOUND')),
    createSector: (command) => settle(() => commands.createSector(command)),
    updateSector: (command) => settle(() => commands.updateSector(command)),
    setSectorArchived: (command) => settle(() => commands.setSectorArchived(command)),
    saveArea: (command) => settle(() => commands.saveArea(command)),
    saveAvailability: (command) => settle(() => commands.saveAvailability(command)),
  }
}
